import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AppContext';
import { X, Plus, ListMusic, Check } from 'lucide-react';
import { toast } from 'sonner';

export const AddToPlaylistModal = ({ videoId, isOpen, onClose }) => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [addedTo, setAddedTo] = useState(new Set());
  const { user, axiosInstance } = useAuth();


  const fetchPlaylists = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/playlists');
      setPlaylists(res.data || []);
      // Check which playlists already contain this video
      const added = new Set();
      for (const pl of (res.data || [])) {
        if (pl.video_ids && pl.video_ids.includes(videoId)) {
          added.add(pl.id);
        }
      }
      setAddedTo(added);
    } catch {
      setPlaylists([]);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, videoId]);

  useEffect(() => {
    if (isOpen && user) fetchPlaylists();
  }, [isOpen, fetchPlaylists, user]);

  const handleAdd = async (playlistId) => {
    try {
      await axiosInstance.post(`/playlists/${playlistId}/videos/${videoId}`);
      setAddedTo(prev => new Set([...prev, playlistId]));
      toast.success('Added to playlist');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const handleRemove = async (playlistId) => {
    try {
      await axiosInstance.delete(`/playlists/${playlistId}/videos/${videoId}`);
      setAddedTo(prev => {
        const next = new Set(prev);
        next.delete(playlistId);
        return next;
      });
      toast.success('Removed from playlist');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await axiosInstance.post('/playlists', { name: newName.trim() });
      const newPlaylist = res.data;
      // Add video to new playlist
      await axiosInstance.post(`/playlists/${newPlaylist.id}/videos/${videoId}`);
      setPlaylists(prev => [newPlaylist, ...prev]);
      setAddedTo(prev => new Set([...prev, newPlaylist.id]));
      setNewName('');
      toast.success(`Created "${newPlaylist.name}" and added video`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create playlist');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" onClick={onClose} data-testid="playlist-modal">
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-sm bg-zinc-900 rounded-2xl overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h3 className="text-white font-semibold text-lg">Add to Playlist</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="close-playlist-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Create new playlist */}
        <div className="px-5 py-3 border-b border-zinc-800/50">
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="New playlist name..."
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
              data-testid="new-playlist-input"
            />
            <button
              onClick={handleCreate}
              disabled={!newName.trim() || creating}
              className="px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-sm font-medium transition-colors flex items-center gap-1"
              data-testid="create-playlist-btn"
            >
              <Plus className="w-4 h-4" />
              Create
            </button>
          </div>
        </div>

        {/* Playlist list */}
        <div className="max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-zinc-600 border-t-white rounded-full animate-spin" />
            </div>
          ) : playlists.length === 0 ? (
            <div className="text-center py-8 px-5">
              <ListMusic className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">No playlists yet</p>
              <p className="text-zinc-600 text-xs mt-1">Create one above to get started</p>
            </div>
          ) : (
            <div className="py-1">
              {playlists.map(pl => {
                const isAdded = addedTo.has(pl.id);
                return (
                  <button
                    key={pl.id}
                    onClick={() => isAdded ? handleRemove(pl.id) : handleAdd(pl.id)}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-zinc-800/50 transition-colors"
                    data-testid={`playlist-item-${pl.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isAdded ? 'bg-orange-500/20' : 'bg-zinc-800'
                    }`}>
                      {isAdded ? (
                        <Check className="w-5 h-5 text-orange-400" />
                      ) : (
                        <ListMusic className="w-5 h-5 text-zinc-400" />
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className={`text-sm font-medium truncate ${isAdded ? 'text-orange-400' : 'text-white'}`}>
                        {pl.name}
                      </p>
                      <p className="text-xs text-zinc-500">{pl.video_count || 0} videos</p>
                    </div>
                    {isAdded && (
                      <span className="text-[10px] text-orange-400/70 flex-shrink-0">Added</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
